import WebApp from '@twa-dev/sdk'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useEffect } from 'react'
import type { ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { ErrorBoundary } from './components/ErrorBoundary'
import { TabBar } from './components/ui/TabBar'
import { useBackButton } from './hooks/useBackButton'
import { useTelegram } from './hooks/useTelegram'
import { Admin } from './pages/Admin'
import { Calendar } from './pages/Calendar'
import { Export } from './pages/Export'
import { Help } from './pages/Help'
import { History } from './pages/History'
import { Integrations } from './pages/Integrations'
import { Landing } from './pages/Landing'
import { Memory } from './pages/Memory'
import { Notifications } from './pages/Notifications'
import { Settings } from './pages/Settings'
import { TelegramChats } from './pages/TelegramChats'
import { Offer } from './pages/Offer'
import { Privacy } from './pages/Privacy'
import { Profile } from './pages/Profile'
import { Reminders } from './pages/Reminders'
import { Stats } from './pages/Stats'
import { Subscription } from './pages/Subscription'
import { Tasks } from './pages/Tasks'
import { Terms } from './pages/Terms'

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
})

// ── Сброс скролла при навигации ────────────────────────────────────────────────

/**
 * React Router не сбрасывает позицию скролла при смене маршрута, а сам скролл-
 * контейнер (div#app-scroll в Telegram-режиме) переживает переходы, поэтому
 * новая страница открывалась с прокрутки предыдущей. Сбрасываем оба контейнера —
 * и внутренний div, и window (веб-режим лендинга скроллит именно окно).
 */
function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    document.getElementById('app-scroll')?.scrollTo(0, 0)
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

// ── Telegram Mini App ─────────────────────────────────────────────────────────

function AppInner() {
  const { colorScheme } = useTelegram()
  const location = useLocation()
  const navigate = useNavigate()

  // Первая запись в истории сессии имеет key === 'default' — значит пользователь
  // пришёл по прямой ссылке из бота и возвращаться через navigate(-1) некуда.
  const isFirstEntry = location.key === 'default'

  // /calendar — алиас корня, рендерит тот же Calendar, поэтому кнопка «Назад»
  // на нём не нужна: вести с него некуда, кроме той же самой страницы.
  const isRoot = location.pathname === '/' || location.pathname === '/calendar'

  useBackButton(!isRoot, () => {
    if (isFirstEntry) {
      navigate('/', { replace: true })
    } else {
      navigate(-1)
    }
  })

  useEffect(() => {
    const html = document.documentElement
    if (colorScheme === 'light') {
      html.classList.add('light')
    } else {
      html.classList.remove('light')
    }
  }, [colorScheme])

  return (
    <div className="max-w-md mx-auto min-h-dvh flex flex-col bg-bg-primary">
      <ScrollToTop />
      <div id="app-scroll" className="flex-1 overflow-y-auto" style={{ paddingBottom: 'calc(60px + env(safe-area-inset-bottom))' }}>
        <Routes>
          <Route path="/" element={<Calendar />} />
          {/* Алиас для старых ссылок из бота */}
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/memory" element={<Memory />} />
          {/* Не в TabBar — попадаем по прямой ссылке из бота */}
          <Route path="/reminders" element={<Reminders />} />
          <Route path="/history" element={<History />} />
          <Route path="/tasks" element={<Tasks />} />
          <Route path="/integrations" element={<Integrations />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/telegram" element={<TelegramChats />} />
          <Route path="/help" element={<Help />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/offer" element={<Offer />} />
          <Route path="/subscription" element={<Subscription />} />
          <Route path="/stats" element={<Stats />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/export" element={<Export />} />
        </Routes>
      </div>
      <TabBar />
    </div>
  )
}

// ── Web landing (non-Telegram) ────────────────────────────────────────────────

function WebRoutes() {
  return (
    <>
      <ScrollToTop />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route
        path="/privacy"
        element={
          <div className="max-w-md mx-auto min-h-dvh bg-bg-primary">
            <Privacy />
          </div>
        }
      />
      <Route
        path="/terms"
        element={
          <div className="max-w-md mx-auto min-h-dvh bg-bg-primary">
            <Terms />
          </div>
        }
      />
      <Route
        path="/offer"
        element={
          <div className="max-w-md mx-auto min-h-dvh bg-bg-primary">
            <Offer />
          </div>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  )
}

// ── Root ──────────────────────────────────────────────────────────────────────

// initData is populated synchronously by the TWA SDK when running inside Telegram
const isTelegramWebApp = !!WebApp.initData

/**
 * Оборачивает содержимое в ErrorBoundary и сбрасывает его при смене маршрута:
 * ошибка рендера на одной странице не роняет всё приложение, а переход на
 * другую страницу восстанавливает работу.
 */
function BoundaryWithReset({ children }: { children: ReactNode }) {
  const location = useLocation()
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <BoundaryWithReset>
          {isTelegramWebApp ? <AppInner /> : <WebRoutes />}
        </BoundaryWithReset>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
